import { useRef, useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import gsap from 'gsap'
import * as THREE from 'three'
import { useCameraStore } from '../stores/useCameraStore'
import { useInfoPanelStore } from '../stores/useInfoPanelStore'
import { useDevModeStore } from '../stores/useDevModeStore'
import { CAMERA_CONSTRAINTS, TRANSITION_SETTINGS } from '../config/cameraConfig'

interface CameraControllerProps {
  enabled?: boolean
}

export default function CameraController({ enabled = true }: CameraControllerProps) {
  const controlsRef = useRef<OrbitControlsImpl>(null)
  const { camera } = useThree()
  const {
    currentPOI,
    cameraMode,
    setCameraMode,
    constraintsOverride,
    setCameraRef,
    setControlsRef,
    editingPOIId,
    transitionTrigger,
  } = useCameraStore()

  // Get camera control values from dev mode store
  const {
    focalLength,
    dampingFactor,
    rotateSpeed,
    panSpeed,
    zoomSpeed,
    autoRotate,
    autoRotateSpeed,
  } = useDevModeStore()

  // Convert focal length (mm) to vertical FOV (degrees)
  // Using 35mm full-frame equivalent with ~24mm sensor height
  const focalLengthToFov = (fl: number): number => {
    const sensorHeight = 24 // mm (35mm full-frame)
    return 2 * Math.atan(sensorHeight / (2 * fl)) * (180 / Math.PI)
  }

  // Set camera and controls refs in store for DevConsole access
  useEffect(() => {
    setCameraRef(camera)
    return () => setCameraRef(null)
  }, [camera, setCameraRef])

  useEffect(() => {
    if (controlsRef.current) {
      setControlsRef(controlsRef.current)
    }
    return () => setControlsRef(null)
  }, [setControlsRef])

  // Transition to POI when currentPOI changes
  useEffect(() => {
    if (!currentPOI || !controlsRef.current) return

    // Auto-exit POI marker editing mode when navigating to a POI
    if (editingPOIId !== null) {
      const { setEditingPOIId } = useCameraStore.getState()
      setEditingPOIId(null)
    }

    const controls = controlsRef.current

    const targetPosition = new THREE.Vector3(
      currentPOI.position.x,
      currentPOI.position.y,
      currentPOI.position.z
    )
    const targetLookAt = new THREE.Vector3(
      currentPOI.target.x,
      currentPOI.target.y,
      currentPOI.target.z
    )

    // Calculate duration based on distance
    const transitionDuration = TRANSITION_SETTINGS.getDuration(camera.position, targetPosition)
    const transitionEasing = TRANSITION_SETTINGS.easing

    // Set camera mode to transitioning
    setCameraMode('transitioning')

    // Disable controls during transition
    controls.enabled = false

    // Create timeline for smooth transition
    const tl = gsap.timeline({
      onComplete: () => {
        controls.enabled = true
        setCameraMode('orbit')

        // Apply POI constraints if provided
        const constraints = currentPOI.constraints
        if (constraints) {
          if (constraints.minDistance !== undefined) {
            controls.minDistance = constraints.minDistance
          }
          if (constraints.maxDistance !== undefined) {
            controls.maxDistance = constraints.maxDistance
          }
          if (constraints.maxPolarAngle !== undefined) {
            controls.maxPolarAngle = constraints.maxPolarAngle
          }
        }
      },
    })

    // Animate camera position
    tl.to(camera.position, {
      x: targetPosition.x,
      y: targetPosition.y,
      z: targetPosition.z,
      duration: transitionDuration,
      ease: transitionEasing,
    })

    // Animate target (what camera looks at)
    tl.to(
      controls.target,
      {
        x: targetLookAt.x,
        y: targetLookAt.y,
        z: targetLookAt.z,
        duration: transitionDuration,
        ease: transitionEasing,
        onUpdate: () => {
          controls.update()
        },
      },
      '<'
    ) // Start at the same time as position animation

    return () => {
      tl.kill()
    }
  }, [currentPOI, camera, setCameraMode, transitionTrigger])

  // Reset constraints when no POI is selected
  useEffect(() => {
    if (!currentPOI && controlsRef.current) {
      const controls = controlsRef.current

      // Reset to default constraints
      controls.minDistance = CAMERA_CONSTRAINTS.distance.min
      controls.maxDistance = CAMERA_CONSTRAINTS.distance.max
      controls.minPolarAngle = CAMERA_CONSTRAINTS.polarAngle.min
      controls.maxPolarAngle = CAMERA_CONSTRAINTS.polarAngle.max
      controls.minAzimuthAngle = CAMERA_CONSTRAINTS.azimuthAngle.min
      controls.maxAzimuthAngle = CAMERA_CONSTRAINTS.azimuthAngle.max
    }
  }, [currentPOI])

  // Apply constraints override if provided
  useEffect(() => {
    if (constraintsOverride && controlsRef.current) {
      const controls = controlsRef.current
      Object.assign(controls, constraintsOverride)
    }
  }, [constraintsOverride])

  // Update FOV when focal length changes
  useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = focalLengthToFov(focalLength)
      camera.updateProjectionMatrix()
    }
  }, [focalLength, camera])

  // Collapse info panel on manual camera movement
  const handleControlStart = () => {
    useInfoPanelStore.getState().onManualCameraMove()
  }

  return (
    <OrbitControls
      ref={controlsRef}
      enabled={enabled && cameraMode !== 'transitioning' && editingPOIId === null}
      onStart={handleControlStart}
      // Initial target (where camera looks at)
      target={[15.44, 0, 1.12]}
      // Distance limits
      minDistance={CAMERA_CONSTRAINTS.distance.min}
      maxDistance={CAMERA_CONSTRAINTS.distance.max}
      // Angle limits
      minPolarAngle={CAMERA_CONSTRAINTS.polarAngle.min}
      maxPolarAngle={CAMERA_CONSTRAINTS.polarAngle.max}
      minAzimuthAngle={CAMERA_CONSTRAINTS.azimuthAngle.min}
      maxAzimuthAngle={CAMERA_CONSTRAINTS.azimuthAngle.max}
      // Control speeds (from dev mode store)
      rotateSpeed={rotateSpeed}
      panSpeed={panSpeed}
      zoomSpeed={zoomSpeed}
      // Damping (from dev mode store)
      enableDamping={dampingFactor > 0}
      dampingFactor={dampingFactor}
      // Mouse buttons configuration
      mouseButtons={{
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN,
      }}
      // Touch controls for tablets
      touches={{
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY_PAN,
      }}
      // Keyboard controls
      keyPanSpeed={7}
      // Screen space panning (true = pan parallel to screen, better for trackpads)
      screenSpacePanning={false}
      // Auto rotate (from dev mode store)
      autoRotate={autoRotate}
      autoRotateSpeed={autoRotateSpeed}
    />
  )
}
